if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        // Track keyboard closes (Esc) so hide() can tell them apart from
        // pointer closes; keydown fires before ModalDialog's keyup handler.
        this.closedByKeyboard = false;
        this.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.closedByKeyboard = true;
        });

        // Remember where the pointer last was inside the modal (X button or
        // backdrop) so hide() can tell which product card sits under it.
        this.lastPointerPosition = null;
        this.addEventListener('pointerup', (event) => {
          this.lastPointerPosition = { x: event.clientX, y: event.clientY };
        });

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        // The option pills are <label>s for radios that are 0x0 and absolutely
        // positioned, so their static position resolves to the fieldset's
        // top-left corner. The browser's own label activation focuses that
        // input and scrolls it into view, which shifts the popup's info column
        // and crops the title/image. Do the activation ourselves with
        // preventScroll so nothing moves. Keyboard users are unaffected: they
        // focus the input directly and still get scroll-into-view.
        this.addEventListener(
          'click',
          (event) => {
            const label = event.target.closest?.('.ws-qa__pills label.ws-qa__pill');
            if (!label) return;
            const input = this.querySelector(`#${CSS.escape(label.getAttribute('for'))}`);
            if (!input || input.disabled) return;
            event.preventDefault();
            if (!input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            input.focus({ preventScroll: true });
          },
          true
        );
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        const opener = this.openedBy;
        // super.hide() refocuses the opener button. Its resting position is
        // translated below the card's overflow-hidden .card__inner, so the
        // browser scrolls that container to reveal it — and overflow:hidden
        // boxes have no scrollbar, leaving the card's layout permanently
        // shifted (image up, hidden button exposed). Capture the scroll
        // state of the opener's in-card ancestors and restore it after.
        const openerCard = opener && opener.closest('.card-wrapper');
        const scrollState = [];
        if (openerCard) {
          for (let el = opener.parentElement; el; el = el.parentElement) {
            scrollState.push([el, el.scrollTop, el.scrollLeft]);
            if (el === openerCard) break;
          }
        }
        super.hide();
        scrollState.forEach(([el, top, left]) => {
          el.scrollTop = top;
          el.scrollLeft = left;
        });
        // super.hide() restores focus to the opener button — required for
        // keyboard users, but after a pointer-driven close that focus keeps
        // the card's hover-revealed "Choose Options" button pinned visible.
        // Release it so the card returns to its resting state.
        if (!this.closedByKeyboard) {
          if (opener) opener.blur();
          // When the modal disappears the pointer usually lands on a product
          // card (the opener's or a neighbour's), whose :hover would instantly
          // reveal its "Choose Options" button. Hit-test the pointer position
          // now that the modal is hidden and suppress that card's hover
          // effects until the pointer genuinely leaves it. Cleared only on
          // mouseleave — the browser fires a synthetic mouseenter on the card
          // as soon as the overlay vanishes, so mouseenter must not clear it.
          if (this.lastPointerPosition) {
            const under = document.elementFromPoint(this.lastPointerPosition.x, this.lastPointerPosition.y);
            const card = under && under.closest('.card-wrapper');
            if (card) {
              card.classList.add('ws-qa-hover-reset');
              card.addEventListener('mouseleave', () => card.classList.remove('ws-qa-hover-reset'), {
                once: true,
              });
            }
          }
        }
        this.closedByKeyboard = false;
      }

      show(opener) {
        this.modalContent = this.modalContent || this.querySelector('[id^="QuickAddInfo-"]');
        if (!this.modalContent) { window.location.href = opener.getAttribute('data-product-url').split('?')[0]; return; }
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info')
              || responseHTML.querySelector('[data-product-id]')
              || responseHTML.querySelector('.shopify-section > *');

            if (!productElement) {
              window.location.href = opener.getAttribute('data-product-url').split('?')[0];
              return;
            }

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        if (!productElement) return;
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
